import { useState, useEffect, useCallback } from 'react';
import { tendersService } from '../services/api';

export function useTenders({ enabled = true } = {}) {
    const [tenders, setTenders] = useState([]);
    const [loading, setLoading] = useState(enabled);
    const [error, setError] = useState(null);

    const transformTender = (tender) => {
        const profileObj = tender.profile;
        return {
            ...tender,
            clientName: profileObj?.clientName || profileObj?.contactPersonName || tender.title || 'Client #' + tender.id,
            projectName: profileObj?.projectName || tender.title || '',
            manager: tender.assignee ? tender.assignee.username : 'Unassigned',
            source: tender.source?.name || '',
        };
    };

    const fetchTenders = useCallback(async (params = {}) => {
        setLoading(true);
        try {
            const fetchParams = { limit: 'all', ...params };
            Object.keys(fetchParams).forEach(k => {
                if (fetchParams[k] === '' || fetchParams[k] === null || fetchParams[k] === undefined) {
                    delete fetchParams[k];
                }
            });
            const response = await tendersService.getAll(fetchParams);
            const transformedTenders = (response.data || []).map(transformTender);
            setTenders(transformedTenders);
            setError(null);
        } catch (err) {
            console.error('Error fetching tenders:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (enabled) {
            fetchTenders();
        } else {
            setLoading(false);
        }
    }, [fetchTenders, enabled]);

    const addTender = async (tenderData) => {
        const newTender = await tendersService.create(tenderData);
        const transformed = transformTender(newTender);
        setTenders(prev => [transformed, ...prev]);
        return newTender;
    };

    const updateTender = async (id, tenderData) => {
        const updated = await tendersService.update(id, tenderData);
        const transformed = transformTender(updated);
        setTenders(prev => prev.map(t => t.id === id ? { ...t, ...transformed } : t));
        return updated;
    };

    const deleteTender = async (id) => {
        await tendersService.delete(id);
        setTenders(prev => prev.filter(t => t.id !== id));
    };

    const deleteAllTenders = async () => {
        await tendersService.deleteAll();
        setTenders([]);
        await fetchTenders();
    };

    return {
        tenders,
        loading,
        error,
        fetchTenders,
        addTender,
        updateTender,
        deleteTender,
        deleteAllTenders,
    };
}
